// KPI + insight รายวันแบบการ์ดเล็กทีละเรื่อง ข้างช่องค้นหาสินค้า — สไลด์แรกเป็น KPI ตามด้วย insight
// เลื่อนเองทุก 8 วิ ครบ 1 รอบแล้วหยุดที่ insight แรกที่ยังไม่ได้ทำ · หยุดเลื่อนเมื่อชี้เมาส์/กำลังพิมพ์/กดเลื่อนเอง
// คลิกการ์ดเปิดแผงขวาที่มีการ์ด KPI เต็ม + การ์ด insight เต็มทุกเรื่อง (ทำแล้ว/ข้าม/👍👎 กดในแผงนั้น — การ์ดเล็กดูอย่างเดียว กันกดผิด)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Box, Drawer, IconButton, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../adapters/api';
import { timeOf } from './format';
import InsightCard from './InsightCard';
import InsightRowsDrawer, { rowActionFor } from './InsightRowsDrawer';
import { KpiCards, kpiParts } from './KpiCards';
import useInsightTasks, { logInsightEvent } from './useInsightTasks';
import { useInsightMe } from './useInsightInline';

const ROTATE_MS = 8000;
const TYPING_PAUSE_MS = 15000;
const STATUS_TEXT = {
  pending: 'insight ของวันนี้จะพร้อมหลัง 06:00 น.',
  running: 'กำลังเตรียม insight ของวันนี้…',
  not_ready: 'insight ของวันนี้ยังไม่พร้อม',
};
const KPI_SLIDE = { kind: 'kpi', id: 'kpi' };

export default function InsightTicker() {
  const { data: me } = useInsightMe();
  const enabled = Boolean(me?.channels?.home_strip);
  const { data: kpi } = useQuery({
    queryKey: ['insightKpi'],
    queryFn: async () => (await api.get('/api/insight/kpi')).data,
    enabled,
    refetchInterval: 10 * 60 * 1000,
  });
  const { data: daily, actionsFor, readOnly } = useInsightTasks(enabled);
  const tasks = useMemo(() => daily?.tasks || [], [daily]);
  const hasKpi = Boolean(kpi?.sections?.length);
  const slides = useMemo(() => (hasKpi ? [KPI_SLIDE, ...tasks] : tasks), [hasKpi, tasks]);

  const [index, setIndex] = useState(0);
  const [hover, setHover] = useState(false);
  const [autoDone, setAutoDone] = useState(false); // ครบ 1 รอบ หรือผู้ใช้กดเลื่อนเอง = หยุดเลื่อนอัตโนมัติ
  const [typingAt, setTypingAt] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const [rowsTask, setRowsTask] = useState(null);
  const stepsRef = useRef(0);
  const seenRef = useRef(new Set());
  const cardRefs = useRef({});

  const idx = Math.max(0, Math.min(index, slides.length - 1)); // รายการหดลงได้เมื่อโหลดใหม่
  const current = slides[idx];
  const task = current?.kind === 'kpi' ? null : current;

  // กำลังพิมพ์ที่ไหนก็ตาม (เช่นช่องค้นหา) = พักการเลื่อน เพื่อไม่ให้การ์ดขยับดึงสายตา
  useEffect(() => {
    const onKey = () => setTypingAt(Date.now());
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (autoDone || hover || panelOpen || slides.length < 2) return undefined;
    // หน่วงจนพ้นช่วงพิมพ์ล่าสุดก่อน (กดแป้นใหม่ = effect นี้เริ่มนับใหม่)
    const t = setTimeout(() => {
      stepsRef.current += 1;
      if (stepsRef.current >= slides.length) {
        const firstOpen = slides.findIndex(x => x.kind !== 'kpi' && x.status === 'open');
        setIndex(firstOpen >= 0 ? firstOpen : 0);
        setAutoDone(true);
      } else {
        setIndex(i => (i + 1) % slides.length);
      }
    }, Math.max(ROTATE_MS, typingAt + TYPING_PAUSE_MS - Date.now()));
    return () => clearTimeout(t);
  }, [index, autoDone, hover, panelOpen, slides, typingAt]);

  // "เห็น" = การ์ด insight นั้นขึ้นมาให้เห็นจริงบนหน้าสินค้า (server ตัดซ้ำอีกชั้น)
  useEffect(() => {
    if (!task || seenRef.current.has(task.id)) return;
    seenRef.current.add(task.id);
    logInsightEvent(task.id, 'seen_home');
  }, [task]);

  // เปิดแผงแล้วเลื่อนไปที่การ์ดเรื่องที่กำลังดู
  useEffect(() => {
    if (!panelOpen || !current) return undefined;
    const t = setTimeout(() => cardRefs.current[current.id]?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 250);
    return () => clearTimeout(t);
  }, [panelOpen, current]);

  if (!enabled || !current) {
    const text = enabled && STATUS_TEXT[daily?.status];
    return text ? <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>💡 {text}</Typography> : null;
  }

  const go = step => { setAutoDone(true); setIndex(i => (i + step + slides.length) % slides.length); };
  const done = task?.status === 'done';
  const skipped = task?.status === 'skipped';

  let title;
  let subline;
  if (task) {
    title = task.title;
    subline = skipped ? 'ข้ามแล้ว' : done ? 'ทำแล้ว' : task.facts?.lines?.[0] || '';
  } else {
    const parts = kpiParts(kpi);
    title = parts[0] || 'วันนี้ของคุณ';
    const status = STATUS_TEXT[daily?.status];
    subline = [
      ...parts.slice(1),
      !kpi.calendar?.todayIsWorkday ? 'วันนี้เป็นวันหยุดของบริษัท' : status,
      `อัปเดต ${timeOf(kpi.asOf)}`,
    ].filter(Boolean).join(' · ');
  }
  const accent = task ? 'warning' : 'primary';
  const rotating = !autoDone && !hover && !panelOpen && slides.length > 1 && Date.now() - typingAt >= TYPING_PAUSE_MS;

  return (
    <>
      <Paper
        elevation={0}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        sx={{
          position: 'relative', overflow: 'hidden',
          display: 'flex', alignItems: 'center', gap: 1, pl: 0.75, pr: 0.5, height: 48, borderRadius: 2.5,
          border: '1px solid', borderColor: t => alpha(t.palette[accent].main, 0.28),
          background: t => `linear-gradient(90deg, ${alpha(t.palette[accent].main, 0.09)} 0%, ${alpha(t.palette[accent].main, 0.02)} 60%, ${t.palette.background.paper} 100%)`,
          transition: 'border-color .3s, background .3s, box-shadow .2s',
          '&:hover': { boxShadow: t => `0 2px 10px ${alpha(t.palette[accent].main, 0.15)}` },
          minWidth: 0, width: '100%',
        }}
      >
        {/* เนื้อหาสไลด์ — คลิกเพื่อเปิดแผงการ์ดเต็ม · เปลี่ยนสไลด์แล้วค่อย ๆ เลื่อนขึ้นมา */}
        <Box
          key={current.id}
          onClick={() => setPanelOpen(true)}
          sx={{
            flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
            opacity: done || skipped ? 0.6 : 1,
            animation: 'tickerIn .35s ease-out',
            '@keyframes tickerIn': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'none' } },
            '&:hover .ticker-title': { color: `${accent}.dark` },
          }}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: t => alpha(t.palette[accent].main, 0.14), color: `${accent}.main`, flexShrink: 0 }}>
            {task ? <LightbulbOutlinedIcon sx={{ fontSize: 18 }} /> : <InsightsIcon sx={{ fontSize: 18 }} />}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography className="ticker-title" variant="body2" fontWeight={700} noWrap lineHeight={1.3} sx={{ transition: 'color .15s' }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div" lineHeight={1.35}>
              {done && <CheckCircleIcon sx={{ fontSize: 12, color: 'success.main', verticalAlign: '-2px', mr: 0.4 }} />}
              {subline}
            </Typography>
          </Box>
        </Box>

        {slides.length > 1 && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', flexShrink: 0, pl: 0.5,
              borderLeft: '1px solid', borderColor: t => alpha(t.palette[accent].main, 0.2),
            }}
          >
            <IconButton size="small" onClick={() => go(-1)} aria-label="เรื่องก่อนหน้า" sx={{ p: 0.25 }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 30, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
              {idx + 1}/{slides.length}
            </Typography>
            <IconButton size="small" onClick={() => go(1)} aria-label="เรื่องถัดไป" sx={{ p: 0.25 }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* เส้นเวลาด้านล่าง: บอกว่ากำลังจะเปลี่ยนเรื่องเอง (หายไปเมื่อหยุดเลื่อน) */}
        {rotating && (
          <Box
            key={`bar-${idx}-${typingAt}`}
            sx={{
              position: 'absolute', left: 0, bottom: 0, height: 2, width: '100%', transformOrigin: 'left',
              bgcolor: t => alpha(t.palette[accent].main, 0.45),
              animation: `tickerBar ${ROTATE_MS}ms linear forwards`,
              '@keyframes tickerBar': { from: { transform: 'scaleX(0)' }, to: { transform: 'scaleX(1)' } },
            }}
          />
        )}
      </Paper>

      <Drawer anchor="right" open={panelOpen} onClose={() => setPanelOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 440 } } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <InsightsIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            วันนี้ของคุณ{tasks.length ? ` · ${tasks.length} เรื่องที่ควรดู` : ''}
          </Typography>
          <IconButton size="small" onClick={() => setPanelOpen(false)} aria-label="ปิด"><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto' }}>
          {hasKpi && (
            <Box ref={el => { cardRefs.current.kpi = el; }} sx={{ scrollMarginTop: 8 }}>
              <KpiCards kpi={kpi} />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                อัปเดต {timeOf(kpi.asOf)}{!kpi.calendar?.todayIsWorkday ? ' · วันนี้เป็นวันหยุดของบริษัท' : ''}
              </Typography>
            </Box>
          )}
          {!tasks.length && STATUS_TEXT[daily?.status] && (
            <Typography variant="body2" color="text.secondary">{STATUS_TEXT[daily.status]}</Typography>
          )}
          {tasks.map(t => (
            <Box key={t.id} ref={el => { cardRefs.current[t.id] = el; }} sx={{ display: 'flex', scrollMarginTop: 8 }}>
              <InsightCard task={t} minWidth={0} actions={actionsFor(t, { onOpenRows: setRowsTask })} />
            </Box>
          ))}
        </Box>
      </Drawer>

      <InsightRowsDrawer
        key={rowsTask?.id || 'none'}
        open={Boolean(rowsTask)} onClose={() => setRowsTask(null)}
        url={rowsTask ? `/api/insight/tasks/${rowsTask.id}/rows` : null}
        title={rowsTask?.title}
        onDownload={rowsTask ? () => actionsFor(rowsTask).download() : undefined}
        onCopy={rowsTask ? () => logInsightEvent(rowsTask.id, 'copy') : undefined}
        rowAction={rowActionFor(rowsTask, readOnly)}
      />
    </>
  );
}
